import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { apiBaseUrl, prepareAuthHeaders } from "./apiBase";
import { salesApi } from "./salesApi";

export const returnsApi = createApi({
  reducerPath: "returnsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: apiBaseUrl,
    prepareHeaders: (headers) =>
      prepareAuthHeaders(headers, {
        acceptJson: true,
        contentTypeJson: true,
      }),
  }),
  tagTypes: ["Return"],
  endpoints: (builder) => ({
    fetchReturnsList: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") {
            return;
          }

          searchParams.set(key, String(value));
        });

        const queryString = searchParams.toString();
        return queryString ? `returns?${queryString}` : "returns";
      },
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (result) =>
        (result?.items || []).map((item) => ({ type: "Return", id: item.id })),
    }),

    searchReturnSales: builder.query({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        Object.entries(params || {}).forEach(([key, value]) => {
          if (value === undefined || value === null || value === "") {
            return;
          }

          searchParams.set(key, String(value));
        });

        const queryString = searchParams.toString();
        return queryString
          ? `returns/sales-search?${queryString}`
          : "returns/sales-search";
      },
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
    }),

    lookupReturn: builder.mutation({
      query: (body) => ({
        url: "returns/lookup",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
    }),

    createReturn: builder.mutation({
      query: (body) => ({
        url: "returns",
        method: "POST",
        body,
      }),
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      invalidatesTags: (result) =>
        result?.id ? [{ type: "Return", id: result.id }] : [],
      // Sales live in their own API slice; refresh the original sale so its
      // returned items and net value show up without a manual reload.
      async onQueryStarted(body, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        if (body?.originalSaleId) {
          dispatch(
            salesApi.util.invalidateTags([{ type: "Sale", id: body.originalSaleId }])
          );
        }
      },
    }),

    fetchReturnById: builder.query({
      query: (returnId) => `returns/${returnId}`,
      transformResponse: (res) => (res?.success ? res.data : res ?? null),
      providesTags: (_result, _error, returnId) => [{ type: "Return", id: returnId }],
    }),
  }),
});

export const {
  useFetchReturnsListQuery,
  useLazySearchReturnSalesQuery,
  useLookupReturnMutation,
  useCreateReturnMutation,
  useFetchReturnByIdQuery,
} = returnsApi;
